import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { reportKeys } from './Reports.queries';
import { useTranslation } from '../i18n';
import type { ReportFilters } from '../types/api';

// STD-015: write/imperative actions for the Reports domain. Mutations that
// change the report list invalidate it so the grid refreshes automatically.
// Errors are toasted by the global MutationCache handler (see lib/queryClient);
// `meta.errorMessage` supplies the i18n fallback message.
export function useGenerateReport() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('reports.generateFailed') },
    mutationFn: async ({ title, filters }: { title: string; filters: ReportFilters }) => {
      const res = await apiService.generateReport(title, filters);
      if (!res.success) throw new Error(res.error || 'Failed to generate report');
      return res.report;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportKeys.list() }),
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('reports.failedToDelete') },
    mutationFn: async (reportId: string) => {
      const res = await apiService.deleteReport(reportId);
      if (!res.success) throw new Error(res.message || 'Failed to delete report');
      return res;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: reportKeys.list() }),
  });
}

// Download returns a short-lived presigned URL — an imperative action, not
// cached state, so it's a mutation. Kept here so the component never imports
// apiService directly (STD-016).
export function useDownloadReport() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('reports.failedToDownload') },
    mutationFn: async (reportId: string) => {
      const res = await apiService.downloadReport(reportId);
      if (!res.success || !res.download_url) {
        throw new Error(res.error || 'Failed to generate download URL');
      }
      return res.download_url;
    },
  });
}
